import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Container
} from '@mui/material';
import { CheckCircle as CheckIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { SubscriptionOfferService } from '../Services/SubscriptionOfferService';
import Helper from '../helpers';

const AcceptSubscriptionOffer = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const checkLoginStatus = useCallback(async () => {
    try {
      const token = await Helper.getCookie('wh-jwt');
      // An expired login counts as logged out, so the person is sent to log in again instead of failing on accept
      setIsLoggedIn(!!token && Helper.parseJwt(token)?.exp > Date.now() / 1000);
    } catch (err) {
      setIsLoggedIn(false);
    }
  }, []);

  const loadOfferDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await SubscriptionOfferService.getOfferDetails(inviteCode);

      if (response.status === 'success' && response.offer) {
        const offer = response.offer;
        setOfferDetails(offer);

        if (!offer.isValid) {
          if (offer.isExpired) {
            setError('This subscription offer has expired.');
          } else {
            setError('This subscription offer is no longer valid.');
          }
        }
      } else {
        setError('Invalid subscription offer code.');
      }
    } catch (err) {
      console.error('Error loading offer:', err);
      setError('Failed to load subscription offer details. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  }, [inviteCode]);

  useEffect(() => {
    checkLoginStatus();
    loadOfferDetails();
  }, [checkLoginStatus, loadOfferDetails]);

  const handleAccept = async () => {
    if (!isLoggedIn) {
      // Redirect to login with return URL
      navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    try {
      setAccepting(true);
      setError('');

      // Accept the offer (validates it's for the right user)
      const acceptResponse = await SubscriptionOfferService.acceptOffer(inviteCode);
      
      if (acceptResponse.status === 'success') {
        // Initiate GoCardless payment flow
        const paymentResponse = await SubscriptionOfferService.initiateOfferPayment(inviteCode);
        
        if (paymentResponse.status === 'success' && paymentResponse.paymentUrl) {
          // Redirect to GoCardless
          window.location.href = paymentResponse.paymentUrl;
        } else {
          setError('Failed to initiate payment setup. Please try again.');
        }
      } else {
        setError(acceptResponse.message || 'Failed to accept offer. Please try again.');
      }
    } catch (err) {
      console.error('Error accepting offer:', err);
      setError(err.message || 'An error occurred while accepting the offer. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!offerDetails) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{error || 'Offer not found'}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
              Subscription Offer
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {!error && offerDetails.isValid && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {offerDetails.venueName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    wants to offer you a subscription
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    {offerDetails.subscriptionName}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
                    <Chip 
                      icon={<CheckIcon />} 
                      label={`${offerDetails.tokens} Tokens`} 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`£${offerDetails.cycleCost.toFixed(2)} every ${offerDetails.cycleDescription || 'cycle'}`}
                      color="secondary"
                      variant="outlined"
                    />
                    {offerDetails.isCustomOffer && (
                      <Chip 
                        label="Custom Offer" 
                        color="success" 
                        size="small"
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {offerDetails.nextCollectionDate
                      ? `Your first payment is planned for ${new Date(offerDetails.nextCollectionDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}.`
                      : 'Your first payment date is confirmed when you set up your Direct Debit.'}
                  </Typography>
                  {offerDetails.expiresOn && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Offer expires: {new Date(offerDetails.expiresOn).toLocaleDateString('en-GB')}
                    </Typography>
                  )}
                </Box>

                {new URLSearchParams(window.location.search).get('cancelled') === 'true' && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    You stopped before finishing your Direct Debit set up. Nothing has been taken. Accept the offer again when you are ready.
                  </Alert>
                )}

                {!isLoggedIn ? (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    You need to log in to accept this subscription offer
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Clicking "Accept Offer" will redirect you to GoCardless to set up your payment mandate
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleAccept}
                  disabled={accepting}
                  endIcon={accepting ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                  sx={{ mt: 2 }}
                >
                  {accepting ? 'Processing...' : (isLoggedIn ? 'Accept Offer' : 'Log In to Accept')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AcceptSubscriptionOffer;
